import React, { useState } from 'react';
import './Chat.css';

interface Message {
  role: string;
  content: string;
  id?: string;
}

export const Chat: React.FC = () => {
  const [inputMessage, setInputMessage] = useState('');
  const [messages, setMessages] = useState<Message[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  // TODO: Replace with new RAG chatbot backend
  // const callGenie = async (messages: Message[]) => {
  //   console.log('Calling Genie API with messages:', messages);
  //
  //   try {
  //     const res = await fetch("https://genie-app2.azurewebsites.net/api/http_trigger_chris", {
  //       method: "POST",
  //       mode: "cors",
  //       headers: {
  //         "Content-Type": "application/json",
  //       },
  //       body: JSON.stringify({ "messages": messages }),
  //     });
  //
  //     console.log('Response status:', res.status);
  //     console.log('Response headers:', res.headers);
  //
  //     if (!res.ok) {
  //       const errorText = await res.text();
  //       console.error('API Error Response:', errorText);
  //       throw new Error(`Genie call failed: ${res.status} - ${errorText}`);
  //     }
  //
  //     const responseData = await res.json();
  //     console.log('API Response data:', responseData);
  //     return responseData;
  //   } catch (error) {
  //     console.error('Fetch error:', error);
  //     throw error;
  //   }
  // };

  const callChatApi = async (messages: Message[]) => {
    // Get the last user message
    const lastUserMessage = messages.filter(m => m.role === 'user').pop();
    if (!lastUserMessage) {
      return { response: "No message provided" };
    }

    const apiUrl = process.env.NODE_ENV === 'production'
      ? 'https://ceaustin117-personal-website-backend.hf.space/ask'
      : 'http://localhost:7860/ask';

    const response = await fetch(apiUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ question: lastUserMessage.content }),
    });

    if (!response.ok) {
      throw new Error('Failed to get response');
    }

    const data = await response.json();
    return { response: data.answer };
  };

  const handleSendMessage = async () => {
    if (!inputMessage.trim()) return;

    const userMessage: Message = {
      role: 'user',
      content: inputMessage,
      id: Date.now().toString()
    };

    // Add user message to chat
    const updatedMessages = [...messages, userMessage];
    setMessages(updatedMessages);
    setInputMessage('');
    setIsLoading(true);

    try {
      const response = await callChatApi(updatedMessages);

      // Extract the message from the API response
      let assistantContent = response.response || 'No response received';
      
      const assistantMessage: Message = {
        role: 'assistant',
        content: assistantContent,
        id: (Date.now() + 1).toString()
      };

      setMessages([...updatedMessages, assistantMessage]);
    } catch (error) {
      console.error('Error calling chat API:', error);

      const errorResponse: Message = {
        role: 'assistant',
        content: 'Sorry, I encountered an error. Please try again.',
        id: (Date.now() + 1).toString()
      };
      setMessages([...updatedMessages, errorResponse]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <div className="chat-container">
      <div className="chat-header">
        <h3>Interactive Chat</h3>
        <p>Try asking about my projects</p>
      </div>
      
      <div className="chat-messages">
        {messages.length === 0 && (
          <div className="chat-placeholder">
            <p>Try asking about my projects, skills, or experience</p>
          </div>
        )}
        
        {messages.map((message) => (
          <div 
            key={message.id} 
            className={`message ${message.role === 'user' ? 'user-message' : 'assistant-message'}`}
          >
            <div className="message-content">
              {message.content}
            </div>
          </div>
        ))}
        
        {isLoading && (
          <div className="message assistant-message">
            <div className="message-content">
              <div className="loading-dots">
                <span></span>
                <span></span>
                <span></span>
              </div>
            </div>
          </div>
        )}
      </div>
      
      <div className="chat-input-container">
        <textarea
          value={inputMessage}
          onChange={(e) => setInputMessage(e.target.value)}
          onKeyPress={handleKeyPress}
          placeholder="Type your message here..."
          disabled={isLoading}
          rows={1}
        />
        <button 
          onClick={handleSendMessage}
          disabled={isLoading || !inputMessage.trim()}
          className="send-button"
        >
          Send
        </button>
      </div>
    </div>
  );
};
